(() => {
  'use strict';

  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');
  const REDUCED_MOTION_MQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  class PdpReviewsMarqueeCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.pdp-reviews-marquee__track');
      this.slides = Array.from(this.querySelectorAll('.pdp-reviews-marquee__card'));
      this.dotsWrap = this.querySelector('.pdp-reviews-marquee__dots');
      this.btnPrev = this.querySelector('.pdp-reviews-marquee__nav-btn--prev');
      this.btnNext = this.querySelector('.pdp-reviews-marquee__nav-btn--next');

      if (!this.track || this.slides.length < 2) {
        this.hideControls();
        return;
      }

      this.currentIndex = 0;
      this.scrollRaf = null;

      this.onScroll = this.onScroll.bind(this);
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);

      this.buildDots();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.btnPrev?.addEventListener('click', this.onPrev);
      this.btnNext?.addEventListener('click', this.onNext);
      this.updateControls();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.btnPrev?.removeEventListener('click', this.onPrev);
      this.btnNext?.removeEventListener('click', this.onNext);
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    }

    hideControls() {
      this.dotsWrap?.remove();
      this.btnPrev?.closest('.pdp-reviews-marquee__nav')?.remove();
    }

    buildDots() {
      if (!this.dotsWrap) return;
      this.dots = this.slides.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pdp-reviews-marquee__dot';
        dot.setAttribute('aria-label', `Go to review ${index + 1}`);
        dot.addEventListener('click', () => this.goTo(index));
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }

    slideStep() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return this.slides[0].getBoundingClientRect().width + gap;
    }

    activeIndex() {
      const step = this.slideStep();
      if (!step) return 0;
      return Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / step)));
    }

    goTo(index) {
      const clamped = Math.min(this.slides.length - 1, Math.max(0, index));
      this.track.scrollTo({
        left: clamped * this.slideStep(),
        behavior: REDUCED_MOTION_MQ.matches ? 'auto' : 'smooth'
      });
    }

    onPrev() {
      this.goTo(this.activeIndex() - 1);
    }

    onNext() {
      this.goTo(this.activeIndex() + 1);
    }

    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.updateControls();
      });
    }

    updateControls() {
      if (!MOBILE_MQ.matches) return;
      const index = this.activeIndex();
      if (index === this.currentIndex && this.dots?.[index]?.classList.contains('pdp-reviews-marquee__dot--active')) {
        return;
      }
      this.currentIndex = index;

      this.dots?.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('pdp-reviews-marquee__dot--active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.btnPrev) this.btnPrev.disabled = index === 0;
      if (this.btnNext) this.btnNext.disabled = index === this.slides.length - 1;
    }
  }

  if (!customElements.get('pdp-reviews-marquee-carousel')) {
    customElements.define('pdp-reviews-marquee-carousel', PdpReviewsMarqueeCarousel);
  }
})();
